import type { IframePoolOrderItem } from '../../../../modules/api';
import { VaultCardRow } from '../cards/VaultCardRow';
import { CountdownLine } from '../cards/CountdownLine';
import { EmptyState } from '../EmptyState';

export interface DropsTabProps {
  items: IframePoolOrderItem[];
  /** First-ever load of this feed — the only state that gets a skeleton. A
   *  reload behind an already-rendered tab must not, see useInfinitePagination. */
  initialLoading: boolean;
  loadingMore: boolean;
  reachedEnd: boolean;
  /** Sentinel ref from useInfinitePagination — attach to a node at the
   *  end of the list to drive infinite scroll. */
  sentinelRef: (node: Element | null) => void;
  onBrowse: () => void;
  /** Tap on a row opens that card's page. */
  onOpenItem: (item: IframePoolOrderItem) => void;
}

const SKELETON_COUNT = 4;

function RowSkeleton() {
  return (
    <div
      data-co-component="MyPacksSkeleton"
      className="h-[135px] w-full animate-pulse rounded-[18px] bg-[var(--co-MyPacksSkeleton-fill,rgba(255,255,255,0.05))]"
    />
  );
}

/**
 * Cards tab — the Vault's opened pool_items as a one-column list (partner
 * Figma 2026-10-08): each row shows the card, its value and the time left
 * before it is sold, and opens the card's page. The UI label is "Cards"; the
 * internal name stays `Drops` to match the backend term and call sites.
 */
export function DropsTab({ items, initialLoading, loadingMore, reachedEnd, sentinelRef, onBrowse, onOpenItem }: DropsTabProps) {
  if (initialLoading) {
    // The list's own column, so the rows don't jump in from the full width.
    return (
      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-3">
        {Array.from({ length: SKELETON_COUNT }, (_, slot) => (
          <RowSkeleton key={slot} />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        title="No cards found"
        description="When cards are vaulted, they'll appear here."
        cta="Buy packs"
        onAction={onBrowse}
      />
    );
  }

  return (
    <>
      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-3">
        {items.map((item) => (
          <VaultCardRow
            key={item.id}
            item={item}
            onOpen={() => onOpenItem(item)}
            footer={<CountdownLine autoSellAt={item.bestActiveOffer?.autoSellAt ?? null} />}
          />
        ))}
        {loadingMore && Array.from({ length: SKELETON_COUNT }, (_, slot) => <RowSkeleton key={`more-${slot}`} />)}
      </div>
      {!reachedEnd && <div ref={sentinelRef} aria-hidden className="h-px" />}
    </>
  );
}
